import { defineStore } from 'pinia'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

// Admin-only store for the calendar heartbeat's OAuth credentials.
// The API is status-only: it returns whether each provider's client_id/secret
// are set and whether the OAuth round-trip has connected, but NEVER the
// secrets or tokens themselves. Writes are therefore one-directional — we POST
// new client creds and read back the refreshed set/not-set status.

export interface CalendarProviderStatus {
  provider: string
  label: string
  connected: boolean
  client_configured: boolean
  scope: string | null
  access_token_expires_at: string | null
  last_polled_at: string | null
  last_poll_error: string | null
  updated_at: string | null
}

export const useCalendarCredentialsStore = defineStore('calendarCredentials', () => {
  const {
    data: providers,
    loaded,
    loading,
    error: loadError,
    refresh: fetchAll,
  } = useAsyncResource<CalendarProviderStatus[]>(
    async () => {
      const res = await fetch('/api/v1/calendar_credentials', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load calendar credentials: ${res.status}`)
      return res.json()
    },
    { initialValue: [] },
  )

  function replace(updated: CalendarProviderStatus): void {
    providers.value = providers.value.map((p) => (p.provider === updated.provider ? updated : p))
  }

  // Persist the client id/secret. Blank fields are ignored server-side so one
  // secret can be rotated without re-entering the other.
  async function saveClient(
    provider: string,
    oauthClientId: string,
    oauthClientSecret: string,
  ): Promise<CalendarProviderStatus> {
    const updated = await apiMutate<CalendarProviderStatus>(`/api/v1/calendar_credentials/${provider}`, {
      method: 'PATCH',
      body: { calendar_credential: { oauth_client_id: oauthClientId, oauth_client_secret: oauthClientSecret } },
      failureLabel: 'failed to save calendar credentials',
    })
    replace(updated)
    return updated
  }

  // Disconnect = clear the stored tokens (keeps the configured client creds).
  async function disconnect(provider: string): Promise<CalendarProviderStatus> {
    const updated = await apiMutate<CalendarProviderStatus>(`/api/v1/calendar_credentials/${provider}`, {
      method: 'DELETE',
      failureLabel: 'failed to disconnect provider',
    })
    replace(updated)
    return updated
  }

  return { providers, loaded, loading, loadError, fetchAll, saveClient, disconnect }
})
